// What this device is, decided once at start: a phone, a tablet or a computer, and roughly how strong its GPU is.
// Romp runs on anything with a browser, a GPU and a camera — an old Android tablet under a TV, a phone propped on a
// shelf, a laptop, a gaming PC — and the same build has to feel right on all of them. The tier only sets starting
// points (render resolution, glow, shadows, which tracker model to try first); what the device then actually measures
// in play overrides it: the renderer drops to lean by itself (engine.ts) and the tracker drops to its fast model by
// itself (pose.ts `autoModel`). Reported with every round, so the numbers say which guesses were wrong.
export type Tier = 'high' | 'mid' | 'low';

const ua = globalThis.navigator?.userAgent ?? '';
const touch = (globalThis.navigator?.maxTouchPoints ?? 0) > 1;
// iPadOS asks for desktop sites and says "Macintosh"; only the touch screen gives it away.
export const ios = /iPhone|iPad|iPod/.test(ua) || (touch && /Macintosh/.test(ua));
export const phone = /iPhone|iPod/.test(ua) || (/Android/.test(ua) && /Mobile/.test(ua));
export const handheld = phone || ios || /Android/.test(ua) || touch;
// Most players arrive by tapping a link inside Instagram (or Facebook, TikTok, Snapchat…), whose built-in browser
// often cannot use the camera, cannot install, and loses saved progress. Those get sent out to the real browser first.
export const inApp = /Instagram|FBAN|FBAV|FB_IAB|Messenger|musical_ly|BytedanceWebview|TikTok|Snapchat|Line\/|Twitter|LinkedInApp|Pinterest/i.test(ua)
  || (/Android/.test(ua) && /; wv\)/.test(ua)) || /[?&]inapp\b/.test(globalThis.location?.search ?? ''); // ?inapp: see the screen anywhere
// Already opened from the home screen: no install button.
export const installed = typeof matchMedia !== 'undefined' && (matchMedia('(display-mode: standalone)').matches || matchMedia('(display-mode: fullscreen)').matches || (globalThis.navigator as Navigator & { standalone?: boolean })?.standalone === true);

function gpuName() {
  try {
    const gl = document.createElement('canvas').getContext('webgl2') as WebGL2RenderingContext | null;
    if (!gl) return 'no webgl2';
    const info = gl.getExtension('WEBGL_debug_renderer_info');
    const name = String(info ? gl.getParameter(info.UNMASKED_RENDERER_WEBGL) : gl.getParameter(gl.RENDERER));
    gl.getExtension('WEBGL_lose_context')?.loseContext();
    return name;
  } catch { return 'unknown'; }
}
export const gpu = typeof document === 'undefined' ? 'none' : gpuName();

// Known-weak GPUs and starved machines start low; real desktop GPUs, Apple silicon and recent flagship phone GPUs
// start high; everything else starts in the middle and is left to measure itself.
function guess(): Tier {
  const cores = navigator.hardwareConcurrency ?? 4, memory = (navigator as Navigator & { deviceMemory?: number }).deviceMemory ?? 4;
  if (/SwiftShader|llvmpipe|softpipe|Software|no webgl2/i.test(gpu)) return 'low';
  if (/Mali-(4|T|G[0-5]\d\b)|Adreno \(TM\) [3-5]\d\d|PowerVR|Intel.*HD Graphics [2-5]\d{2,3}\b|Intel.*HD Graphics$/i.test(gpu) || memory <= 2 || cores <= 2) return 'low';
  if (/NVIDIA|GeForce|Radeon|AMD|Apple M\d|Adreno \(TM\) (7[3-9]\d|8\d\d)|Mali-G(7[1-9]|71\d|72\d|9\d\d)|Immortalis|Arc/i.test(gpu) && cores >= 6) return 'high';
  return 'mid';
}
const forced = (() => { try { return localStorage.getItem('romp.tier'); } catch { return null; } })();
export const tier: Tier = forced === 'high' || forced === 'mid' || forced === 'low' ? forced : guess();

export const device = { tier, gpu, phone, ios, handheld, inApp, installed, cores: globalThis.navigator?.hardwareConcurrency ?? 0 };
